import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { useCommerceAPI } from '~/features/commerce-api/contexts';
import {
    SalesforceProduct,
    SalesforceProductPriceType,
    SalesforcePromotionCountdown,
} from '~/lib/salesforce/types';
import { usePromotionCountdown } from '~/shared/hooks/countdown';
import { useFrame } from '~/shared/utils';
import { toOre } from '~/shared/utils/numbers';
import { PRODUCT_DATA_GC_TIME, PRODUCT_DATA_STALE_TIME } from './constants';
import { usePrelaunchGate } from './hooks/usePrelaunchGate';
import { useSelectedProductId } from './hooks/useSelectedProductId';
import { useVariantGroups } from './hooks/useVariantGroups';
import { useVariantPrefetch } from './hooks/useVariantPrefetch';
import { ProductInfoLabelDialogContent, ProductTab, VariantGroup, VariantOption } from './types';
import { getInitialDataUpdatedAt, productQueryKey } from './utilities';

/**
 * Splitting up the context values to prevent unnecessary re-renders.
 */
interface PDPDataContextValue {
    product: SalesforceProduct;
    variantGroups: VariantGroup[];
    currency: string;
    isCustomizableWithSpiff: boolean;
    yotpoIdentifier: string;
    /**
     * Sold out online and in stores with no restock expected. Distinct from a discontinued
     * product, which is redirected away before the page renders.
     */
    isSoldOutPermanently: boolean;
    /** Sold out online with no restock expected, while stores still hold stock. */
    isSoldOutPermanentlyOnline: boolean;
}

const PDPDataContext = createContext<PDPDataContextValue | undefined>(undefined);

/**
 * State that describes a switch in progress rather than the product itself. It is kept apart
 * from the data because it changes twice per variant click while `product` does not: only two
 * components read it, against the ~29 that read the product.
 */
interface PDPStatusContextValue {
    isProductDataLoading: boolean;
    /** The product being switched to, which leads `product` while its data is in flight. */
    selectedProductId: string | undefined;
    /** Derived here so no caller has to remember every reason buying might be held back. */
    areProductActionsDisabled: boolean;
}

const PDPStatusContext = createContext<PDPStatusContextValue | undefined>(undefined);

interface PDPStateContextValue {
    isNotificationDialogOpen: boolean;
    isReservationDrawerOpen: boolean;
    infoLabelDialog: ProductInfoLabelDialogContent | undefined;
    activeProductTab: ProductTab;
    /**
     * A standing request for one tab's panel to be shown in full rather than clipped. Cleared by
     * whoever honours it, so asking twice works.
     */
    productTabToExpand: ProductTab | undefined;
    /** The offer is announced but not yet buyable. Worked out from the product's launch date. */
    isPrelaunchBlockingBuy: boolean;
    /**
     * A countdown is on screen. Stays true for a day after launch, so anything making room for
     * it - the sticky bar's image - should use this, not `isPrelaunchBlockingBuy`.
     */
    isPrelaunchCountdownVisible: boolean;
    /**
     * A promotion announced for the product but not discounting it yet. Salesforce already prices
     * the product with it, so until it starts the page shows the regular price instead.
     */
    promotionCountdown?: SalesforcePromotionCountdown;
    /**
     * The price list shows the discount of the promotion counting down, which is held back: prices,
     * splash, campaign dates and the schema show the regular price until it starts.
     */
    isPromotionDiscountHeldBack: boolean;
}

const PDPStateContext = createContext<PDPStateContextValue | undefined>(undefined);

/**
 * Salesforce builds the price list from the product's highest-ranked promotion only. The discount
 * is held back when that is the promotion counting down, not when another, such as a member price,
 * outranks it: that one stays valid and shown during the countdown.
 */
const getIsPromotionDiscountHeldBack = (
    product?: SalesforceProduct,
    promotionCountdown?: SalesforcePromotionCountdown,
): boolean => {
    if (!product || !promotionCountdown?.isApplicableToProduct) return false;

    const { discountedPrice, promotionType } = promotionCountdown;

    // Without a unit price (e.g. mix and match) there is no price to match, only the type shown.
    if (discountedPrice === null) return product.c_activepromo?.type === promotionType;

    return !!product.c_prices?.some(
        ({ type, value }) =>
            type === SalesforceProductPriceType.SALE && toOre(value) === toOre(discountedPrice),
    );
};

interface PDPActionsContextValue {
    selectVariant(option: VariantOption): void;
    setIsNotificationDialogOpen(value: boolean): void;
    setIsReservationDrawerOpen(value: boolean): void;
    setInfoLabelDialog(value: ProductInfoLabelDialogContent | undefined): void;
    setActiveProductTab(tab: ProductTab): void;
    setProductTabToExpand(tab: ProductTab | undefined): void;
}

const PDPActionsContext = createContext<PDPActionsContextValue | undefined>(undefined);

interface PDPProviderProps {
    initialData: SalesforceProduct | null | undefined;
    initialDataTimestamp?: number;
}

export const PDPProvider: React.FC<PropsWithChildren<PDPProviderProps>> = ({
    children,
    initialData,
    initialDataTimestamp,
}) => {
    const { shopperProducts } = useCommerceAPI();
    const { data: frame } = useFrame();

    const currency = frame?.market?.currency?.currency ?? 'DKK';

    const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);
    const [isReservationDrawerOpen, setIsReservationDrawerOpen] = useState(false);
    const [infoLabelDialog, setInfoLabelDialog] = useState<ProductInfoLabelDialogContent>();
    const [activeProductTab, setActiveProductTab] = useState(ProductTab.DESCRIPTION);
    const [productTabToExpand, setProductTabToExpand] = useState<ProductTab | undefined>();

    const { productId, selectVariant } = useSelectedProductId(initialData);
    const [lastResetProductId, setLastResetProductId] = useState(productId);

    // Reset UI states when switching to a different product.
    if (lastResetProductId !== productId) {
        setLastResetProductId(productId);
        setIsNotificationDialogOpen(false);
        setIsReservationDrawerOpen(false);
        setInfoLabelDialog(undefined);
        setActiveProductTab(ProductTab.DESCRIPTION);
        setProductTabToExpand(undefined);
    }

    const {
        data: product,
        isLoading,
        isPlaceholderData,
    } = useQuery({
        queryKey: productQueryKey(productId),
        queryFn: async () => {
            if (!productId) return undefined;

            const response = await shopperProducts?.getProducts({
                parameters: {
                    ids: productId,
                    allImages: true,
                    currency,
                },
            });

            // ASSERTION: We are confident that Shopper Products (commerce-sdk-isomorphic) will return SalesforceProduct.
            return response?.data?.[0] as SalesforceProduct;
        },
        enabled: !!productId && !!shopperProducts,
        staleTime: PRODUCT_DATA_STALE_TIME,
        gcTime: PRODUCT_DATA_GC_TIME,
        // We will only use the initialData if it still matches the active product ID.
        initialData: productId === initialData?.id ? (initialData ?? undefined) : undefined,
        initialDataUpdatedAt: getInitialDataUpdatedAt(initialDataTimestamp),
        placeholderData: keepPreviousData,
    });

    const variantGroups = useVariantGroups(product);

    // Prefetch variants to make transitions to other variants smooth.
    useVariantPrefetch(product, currency);

    // A product can be customized with Spiff if it is customizeable and the feature is enabled in Umbraco.
    const isCustomizableWithSpiff =
        !!product?.c_personalization &&
        frame?.productSettings?.shouldAllowPersonalization !== false;

    const isProductDataLoading = isLoading || isPlaceholderData;

    const { isBlockingBuy: isPrelaunchBlockingBuy, isCountdownVisible: isPrelaunchCountdownVisible } =
        usePrelaunchGate(product?.c_prelaunch);

    // Not fetched again at zero: the discounted prices are already in the product, so the page
    // switches on its own, and every open page asking at the same second is what we avoid.
    const promotionCountdown = usePromotionCountdown(product?.c_promotionCountdown);
    const isPromotionDiscountHeldBack = getIsPromotionDiscountHeldBack(product, promotionCountdown);

    const dataValue = useMemo<PDPDataContextValue | undefined>(() => {
        if (!product) return undefined;

        // Salesforce states the sold-out cases on the buy button rather than as stock fields.
        const primaryButtonKey = product.c_buyButtonsObj?.c_primaryButton?.translationKey;

        return {
            product,
            variantGroups,
            currency,
            isCustomizableWithSpiff,
            yotpoIdentifier: `product-${product.id}-reviews`,
            isSoldOutPermanently: primaryButtonKey === 'outofstockpermanently',
            isSoldOutPermanentlyOnline: primaryButtonKey === 'outofstockpermanentlyonline',
        };
    }, [product, variantGroups]);

    const statusValue = useMemo<PDPStatusContextValue>(
        () => ({
            isProductDataLoading,
            selectedProductId: productId,
            areProductActionsDisabled: isPrelaunchBlockingBuy || isProductDataLoading,
        }),
        [isProductDataLoading, isPrelaunchBlockingBuy, productId],
    );

    const uiStateValue = useMemo<PDPStateContextValue>(
        () => ({
            isNotificationDialogOpen,
            isReservationDrawerOpen,
            infoLabelDialog,
            activeProductTab,
            productTabToExpand,
            isPrelaunchBlockingBuy,
            isPrelaunchCountdownVisible,
            promotionCountdown,
            isPromotionDiscountHeldBack,
        }),
        [
            isNotificationDialogOpen,
            isReservationDrawerOpen,
            infoLabelDialog,
            activeProductTab,
            productTabToExpand,
            isPrelaunchBlockingBuy,
            isPrelaunchCountdownVisible,
            promotionCountdown,
            isPromotionDiscountHeldBack,
        ],
    );

    const actionsValue = useMemo<PDPActionsContextValue>(
        () => ({
            selectVariant,
            setIsNotificationDialogOpen,
            setIsReservationDrawerOpen,
            setInfoLabelDialog,
            setActiveProductTab,
            setProductTabToExpand,
        }),
        [selectVariant],
    );

    return (
        <PDPActionsContext.Provider value={actionsValue}>
            <PDPDataContext.Provider value={dataValue}>
                <PDPStatusContext.Provider value={statusValue}>
                    <PDPStateContext.Provider value={uiStateValue}>
                        {children}
                    </PDPStateContext.Provider>
                </PDPStatusContext.Provider>
            </PDPDataContext.Provider>
        </PDPActionsContext.Provider>
    );
};

const usePDPContext = <T,>(context: React.Context<T | undefined>, hookName: string): T => {
    const value = useContext(context);

    if (!value) {
        throw new Error(
            `${hookName}() can only be used inside <PDPProvider />, and only once product data is available.`,
        );
    }

    return value;
};

export const usePDPData = () => usePDPContext(PDPDataContext, 'usePDPData');

/**
 * For consumers that can render either on a product page or off it, such as CMS blocks.
 * Returns undefined instead of throwing when there is no product in context.
 */
export const useOptionalPDPData = () => useContext(PDPDataContext);
export const usePDPStatus = () => usePDPContext(PDPStatusContext, 'usePDPStatus');
export const usePDPState = () => usePDPContext(PDPStateContext, 'usePDPState');
export const usePDPActions = () => usePDPContext(PDPActionsContext, 'usePDPActions');
